<template>
  <div class="cad-dual-page">
    <section class="cad-dual-stage-column">
      <header class="cad-dual-header">
        <div class="cad-dual-copy">
          <p class="cad-dual-kicker">CAD Multi-Viewport Sandbox</p>
          <h1 class="cad-dual-title">双视口 CAD 联调示例</h1>
          <p class="cad-dual-description">
            左右两个视口共享同一个 CAD 场景，但相机与控制器独立。点击任一视口后，
            继续使用右侧 CAD 面板即可在当前 active view 中绘制，用来验证多视口下的 CAD
            画图、编辑和辅助工具是否正常。
          </p>
        </div>

        <div class="cad-dual-pill-list">
          <span class="cad-dual-pill">共享 CAD 场景</span>
          <span class="cad-dual-pill cad-dual-pill-accent">点击视口切换 active</span>
          <span class="cad-dual-pill">双视口独立缩放/平移</span>
        </div>
      </header>

      <div ref="stageShellRef" class="cad-dual-stage-shell">
        <div ref="containerRef" class="cad-dual-canvas-host"></div>

        <div class="cad-dual-viewport-layout">
          <div
            v-for="definition in VIEW_DEFINITIONS"
            :key="definition.id"
            :ref="(element) => setViewElement(definition.id, element)"
            class="cad-dual-viewport-pane"
            :style="{ '--view-accent': definition.accent }"
          >
            <div class="cad-dual-viewport-frame" :class="{ active: activeViewId === definition.id }">
              <button
                class="cad-dual-viewport-tag"
                type="button"
                @pointerdown.prevent
                @click="focusView(definition.id)"
              >
                <span class="cad-dual-viewport-tag-title">{{ definition.title }}</span>
                <span class="cad-dual-viewport-tag-meta">{{ definition.subtitle }}</span>
              </button>

              <span class="cad-dual-viewport-hint">
                {{ activeViewId === definition.id ? '当前绘制视口' : '点击画布即可切换到此视口' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <SceneControlPanel
      layout="sidebar"
      kicker="双视口 CAD"
      title="联调控制台"
      :meta="activeViewDefinition.title"
    >
      <SceneStatusCard
        title="视口状态"
        subtitle="这里汇总当前 active view 和双视口联调状态。"
        :badge="activeViewDefinition.title"
        :items="statusItems"
        hint="CAD 工具是共享的。先在右侧选工具，再点击左/右任一视口开始绘制，就可以验证多视口下的 CAD 输入路由与状态切换。"
        action-label="重置双视口"
        action-variant="neutral"
        @action="resetViews()"
      />

      <GraphicsBackendControlCard :card="graphicsCard" />

      <details class="panel-section" open>
        <summary class="panel-section-summary">
          <span class="summary-main">
            <span class="panel-section-title">视口操作</span>
            <span class="panel-section-subtitle">快速切换 active view 或适配当前 CAD 内容。</span>
          </span>
          <span class="summary-side">
            <span class="summary-count">2 View</span>
            <span class="summary-chevron" aria-hidden="true">▸</span>
          </span>
        </summary>

        <div class="panel-section-body">
          <div class="cad-dual-panel-grid">
            <button
              v-for="definition in VIEW_DEFINITIONS"
              :key="`${definition.id}-focus`"
              class="panel-button"
              :class="{ active: activeViewId === definition.id }"
              @click="focusView(definition.id)"
            >
              <span class="panel-button-label">激活{{ definition.title }}</span>
              <span class="panel-button-meta">{{ definition.shortHint }}</span>
            </button>

            <button
              v-for="definition in VIEW_DEFINITIONS"
              :key="`${definition.id}-fit`"
              class="panel-button panel-button-subtle"
              @click="fitView(definition.id)"
            >
              <span class="panel-button-label">适配{{ definition.title }}</span>
              <span class="panel-button-meta">重新聚焦当前 CAD 内容</span>
            </button>
          </div>
        </div>
      </details>

      <CADPanel
        title="CAD 画图工具"
        subtitle="左右视口共用一套 CAD 工具，但始终只作用于当前 active view。"
      />
    </SceneControlPanel>
  </div>
</template>

<script setup lang="ts">
import * as THREE from 'three'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  useTemplateRef,
  type ComponentPublicInstance,
} from 'vue'
import {
  CAD,
  CameraMode,
  type CadHandle,
  type CadPlugin,
  type CameraRig,
  type ControlMode,
  type ViewHandle,
  type ViewPreset,
  type ViewRect,
} from '@sharefe/share3d-engine'
import CADPanel from './CADPanel.vue'
import GraphicsBackendControlCard from './GraphicsBackendControlCard.vue'
import SceneControlPanel from './SceneControlPanel.vue'
import SceneStatusCard from './SceneStatusCard.vue'
import { usePotreeEngine } from '../hooks'
import { createCadFixturePlugins } from '../utils/cadFixturePlugins'
import { useExampleGraphicsCard } from '../composables/useExampleGraphicsCard'
import { toGraphicsRuntimeError } from '../utils/graphicsRuntimeError'

type ManagedViewId = 'primary' | 'secondary'

interface ManagedViewDefinition {
  id: ManagedViewId
  engineViewId: string
  cadAssetId: string
  cadLayerMask: number
  title: string
  subtitle: string
  shortHint: string
  accent: string
  defaultCameraMode: CameraMode
  defaultControlMode: ControlMode
  defaultPreset: ViewPreset
}

const PRIMARY_VIEW_ID = 'primary'
const PRIMARY_CAD_ASSET_ID = 'cad-primary-session'
const SECONDARY_CAD_ASSET_ID = 'cad-secondary-session'
const PRIMARY_CAD_LAYER_MASK = 1 << 3
const SECONDARY_CAD_LAYER_MASK = 1 << 4
const SECONDARY_VIEW_ID = 'cad-secondary'

const VIEW_DEFINITIONS: ManagedViewDefinition[] = [
  {
    id: 'primary',
    engineViewId: PRIMARY_VIEW_ID,
    cadAssetId: PRIMARY_CAD_ASSET_ID,
    cadLayerMask: PRIMARY_CAD_LAYER_MASK,
    title: '左视口',
    subtitle: '正交 / 地图控制 / 共享 CAD',
    shortHint: '主视口联调位',
    accent: '#8ed0ff',
    defaultCameraMode: CameraMode.Orthographic,
    defaultControlMode: 'map',
    defaultPreset: 'top',
  },
  {
    id: 'secondary',
    engineViewId: SECONDARY_VIEW_ID,
    cadAssetId: SECONDARY_CAD_ASSET_ID,
    cadLayerMask: SECONDARY_CAD_LAYER_MASK,
    title: '右视口',
    subtitle: '正交 / 地图控制 / 共享 CAD',
    shortHint: '副视口联调位',
    accent: '#ffbf7c',
    defaultCameraMode: CameraMode.Orthographic,
    defaultControlMode: 'map',
    defaultPreset: 'top',
  },
]

const containerRef = useTemplateRef<HTMLDivElement>('containerRef')
const stageShellRef = useTemplateRef<HTMLDivElement>('stageShellRef')
const graphicsCard = useExampleGraphicsCard({ presetKey: 'cadFixture' })
const plugins = createCadFixturePlugins()
const viewElements = new Map<ManagedViewId, HTMLElement>()
const activeViewId = ref<ManagedViewId>('primary')
let resizeObserver: ResizeObserver | null = null
let resizeRaf = 0
let detachViewportSyncListeners: (() => void) | null = null
let teardownViewBindings: (() => void) | null = null

const { engine, init, dispose, actualBackend } = usePotreeEngine(containerRef, {
  backend: graphicsCard.requestedBackend.value,
  plugins,
  background: '#060a10',
  cameraMode: CameraMode.Orthographic,
  renderMode: 'on-demand',
})

provide('engine', engine)

const activeViewDefinition = computed(() => {
  return getViewDefinition(activeViewId.value)
})

interface ManagedViewControlManager {
  activeMode: ControlMode
  setMode(mode: ControlMode): void
}

type CadPluginWithContainer = {
  cadContainer?: {
    get(token: unknown): {
      tempGroup?: THREE.Object3D
    }
  }
}

const statusItems = computed(() => {
  return [
    { label: '当前 active', value: activeViewDefinition.value.title },
    { label: '视口数量', value: VIEW_DEFINITIONS.length },
    { label: '相机模式', value: formatCameraMode(getViewRig(activeViewId.value)?.mode) },
    {
      label: '控制器',
      value: getViewControlManager(activeViewId.value)?.activeMode ?? '未就绪',
    },
  ]
})

function getViewDefinition(viewId: ManagedViewId): ManagedViewDefinition {
  return VIEW_DEFINITIONS.find((item) => item.id === viewId) ?? VIEW_DEFINITIONS[0]
}

function getEngineViewId(viewId: ManagedViewId) {
  return getViewDefinition(viewId).engineViewId
}

function getCadPlugin(): CadPlugin | undefined {
  return engine.value?.plugins.get('cad') as CadPlugin | undefined
}

function getCadHandle(viewId: ManagedViewId): CadHandle | undefined {
  const cadPlugin = getCadPlugin()
  if (!cadPlugin) {
    return undefined
  }

  return cadPlugin.getAsset(getViewDefinition(viewId).cadAssetId)
}

function applyMaskRecursive(object: THREE.Object3D | null | undefined, mask: number) {
  if (!object) {
    return
  }

  object.traverse((node) => {
    node.layers.mask = mask
  })
}

function syncCadHandleMask(viewId: ManagedViewId) {
  const definition = getViewDefinition(viewId)
  applyMaskRecursive(getCadHandle(viewId)?.object3D, definition.cadLayerMask)
}

function getCadTempGroup(): THREE.Object3D | null {
  const cadPlugin = getCadPlugin() as unknown as CadPluginWithContainer | undefined
  if (!cadPlugin?.cadContainer) {
    return null
  }

  try {
    return cadPlugin.cadContainer.get(CAD.TYPES.CADViewManager).tempGroup ?? null
  } catch {
    return null
  }
}

function syncCadTempGroupMask(viewId: ManagedViewId) {
  const definition = getViewDefinition(viewId)
  applyMaskRecursive(getCadTempGroup(), definition.cadLayerMask)
}

function syncCadMasks() {
  for (const definition of VIEW_DEFINITIONS) {
    syncCadHandleMask(definition.id)
  }
  syncCadTempGroupMask(activeViewId.value)
}

function syncCadSessionForView(viewId: ManagedViewId) {
  const cadPlugin = getCadPlugin()
  if (!cadPlugin) {
    return
  }

  const definition = getViewDefinition(viewId)
  if (cadPlugin.getActiveId() !== definition.cadAssetId) {
    cadPlugin.setActive(definition.cadAssetId)
  }
  syncCadTempGroupMask(viewId)
}

function setViewElement(
  viewId: ManagedViewId,
  element: Element | ComponentPublicInstance | null,
) {
  if (element instanceof HTMLElement) {
    viewElements.set(viewId, element)
    return
  }

  viewElements.delete(viewId)
}

function toManagedViewId(viewId: string | null | undefined): ManagedViewId {
  return viewId === SECONDARY_VIEW_ID ? 'secondary' : 'primary'
}

function getViewHandle(viewId: ManagedViewId): ViewHandle | undefined {
  return engine.value?.views.get(getEngineViewId(viewId))
}

function getViewRig(viewId: ManagedViewId): CameraRig | null {
  return getViewHandle(viewId)?.getSlot<CameraRig>('cameraRig') ?? null
}

function getViewControlManager(viewId: ManagedViewId): ManagedViewControlManager | null {
  return getViewHandle(viewId)?.getSlot<ManagedViewControlManager>('controlManager') ?? null
}

function formatCameraMode(mode: CameraMode | undefined): string {
  if (mode === CameraMode.Orthographic) {
    return '正交'
  }

  if (mode === CameraMode.Perspective) {
    return '透视'
  }

  return '未就绪'
}

function roundRect(rect: ViewRect): ViewRect {
  return {
    x: Math.round(rect.x),
    y: Math.round(rect.y),
    width: Math.round(rect.width),
    height: Math.round(rect.height),
  }
}

function ensureManagedViews() {
  const engineInstance = engine.value
  if (!engineInstance) {
    return
  }

  const primary = engineInstance.views.get(PRIMARY_VIEW_ID)
  primary?.setLayerMask(PRIMARY_CAD_LAYER_MASK)
  primary?.setUserData({ role: 'cad-primary' })

  const secondary =
    engineInstance.views.get(SECONDARY_VIEW_ID) ??
    engineInstance.views.create({
      id: SECONDARY_VIEW_ID,
      rect: { x: 0, y: 0, width: 1, height: 1 },
      zIndex: 1,
      layerMask: SECONDARY_CAD_LAYER_MASK,
      userData: { role: 'cad-secondary' },
    })

  secondary.setLayerMask(SECONDARY_CAD_LAYER_MASK)
  secondary.setUserData({ role: 'cad-secondary' })
  engineInstance.views.setActive(PRIMARY_VIEW_ID)
}

function getSceneFitBox(viewId: ManagedViewId): THREE.Box3 {
  const box = new THREE.Box3()
  const assetBounds = getCadHandle(viewId)?.boundingBox?.clone()
  if (assetBounds && !assetBounds.isEmpty()) {
    box.union(assetBounds)
  }

  return box
}

function applyDefaultViewConfiguration() {
  for (const definition of VIEW_DEFINITIONS) {
    const handle = getViewHandle(definition.id)
    const rig = getViewRig(definition.id)
    const manager = getViewControlManager(definition.id)

    handle?.setLayerMask(definition.cadLayerMask)
    rig?.setMode(definition.defaultCameraMode)
    rig?.setPresetView(definition.defaultPreset)
    manager?.setMode(definition.defaultControlMode)
  }
}

function focusView(viewId: ManagedViewId) {
  engine.value?.views.setActive(getEngineViewId(viewId))
  syncCadSessionForView(viewId)
  containerRef.value?.focus()
  activeViewId.value = viewId
}

function fitView(viewId: ManagedViewId) {
  const rig = getViewRig(viewId)
  if (!rig) {
    return
  }

  const box = getSceneFitBox(viewId)
  if (box.isEmpty()) {
    rig.setPresetView('top')
    return
  }

  rig.fitToBox(box)
}

function resetViews() {
  applyDefaultViewConfiguration()
  for (const definition of VIEW_DEFINITIONS) {
    fitView(definition.id)
  }
  focusView('primary')
}

function bindEngineEvents() {
  const engineInstance = engine.value
  if (!engineInstance) {
    return
  }

  teardownViewBindings?.()

  const handleViewActivated = (event: { viewId: string }) => {
    const nextViewId = toManagedViewId(event.viewId)
    activeViewId.value = nextViewId
    syncCadSessionForView(nextViewId)
  }

  const handleCadHistoryChanged = () => {
    syncCadMasks()
  }

  const handleFrameBegin = () => {
    syncCadTempGroupMask(activeViewId.value)
  }

  engineInstance.events.on('view.activated', handleViewActivated)
  engineInstance.events.on('cad.historyChanged', handleCadHistoryChanged)
  engineInstance.events.on('frame.begin', handleFrameBegin)
  teardownViewBindings = () => {
    engineInstance.events.off('view.activated', handleViewActivated)
    engineInstance.events.off('cad.historyChanged', handleCadHistoryChanged)
    engineInstance.events.off('frame.begin', handleFrameBegin)
    teardownViewBindings = null
  }
}

function measureViewRect(viewId: ManagedViewId): ViewRect | null {
  const stage = stageShellRef.value
  const element = viewElements.get(viewId)
  if (!stage || !element) {
    return null
  }

  const stageRect = stage.getBoundingClientRect()
  const elementRect = element.getBoundingClientRect()
  const rect = roundRect({
    x: elementRect.left - stageRect.left,
    y: elementRect.top - stageRect.top,
    width: elementRect.width,
    height: elementRect.height,
  })

  if (rect.width <= 0 || rect.height <= 0) {
    return null
  }

  return rect
}

function syncViewRects() {
  const engineInstance = engine.value
  if (!engineInstance) {
    return
  }

  for (const definition of VIEW_DEFINITIONS) {
    const rect = measureViewRect(definition.id)
    if (!rect) {
      continue
    }

    getViewHandle(definition.id)?.setRect(rect)
  }
}

function scheduleViewRectSync() {
  if (resizeRaf !== 0) {
    return
  }

  resizeRaf = window.requestAnimationFrame(() => {
    resizeRaf = 0
    syncViewRects()
  })
}

function setupResizeObserver() {
  if (!stageShellRef.value) {
    return
  }

  resizeObserver?.disconnect()
  resizeObserver = new ResizeObserver(() => {
    scheduleViewRectSync()
  })
  resizeObserver.observe(stageShellRef.value)
}

function setupViewportSyncListeners() {
  detachViewportSyncListeners?.()

  const handleViewportMutation = () => {
    scheduleViewRectSync()
  }

  window.addEventListener('resize', handleViewportMutation)
  window.visualViewport?.addEventListener('resize', handleViewportMutation)

  let detachPixelRatioListener: (() => void) | null = null
  if (typeof window.matchMedia === 'function') {
    const bindPixelRatioListener = () => {
      detachPixelRatioListener?.()
      const query = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`)
      const handleChange = () => {
        scheduleViewRectSync()
        bindPixelRatioListener()
      }
      query.addEventListener('change', handleChange)
      detachPixelRatioListener = () => {
        query.removeEventListener('change', handleChange)
      }
    }

    bindPixelRatioListener()
  }

  detachViewportSyncListeners = () => {
    window.removeEventListener('resize', handleViewportMutation)
    window.visualViewport?.removeEventListener('resize', handleViewportMutation)
    detachPixelRatioListener?.()
    detachPixelRatioListener = null
  }
}

onMounted(async () => {
  graphicsCard.markCreating()
  try {
    await init()
  } catch (error) {
    graphicsCard.markError(toGraphicsRuntimeError(error))
    return
  }
  graphicsCard.markReady(actualBackend.value)

  const engineInstance = engine.value
  if (!engineInstance) {
    return
  }

  ensureManagedViews()
  applyDefaultViewConfiguration()
  await engineInstance.loadCadScene({
    id: PRIMARY_CAD_ASSET_ID,
    name: 'Left CAD Session',
    data: JSON.stringify({
      entities: [],
      layers: [],
    }),
    format: 'json',
  })
  await engineInstance.loadCadScene({
    id: SECONDARY_CAD_ASSET_ID,
    name: 'Right CAD Session',
    data: JSON.stringify({
      entities: [],
      layers: [],
    }),
    format: 'json',
  })
  syncCadMasks()
  syncCadSessionForView('primary')
  engineInstance.activateTool('cad')
  syncCadSessionForView('primary')
  bindEngineEvents()

  await nextTick()
  setupResizeObserver()
  setupViewportSyncListeners()
  scheduleViewRectSync()
  resetViews()
})

onBeforeUnmount(() => {
  teardownViewBindings?.()
  detachViewportSyncListeners?.()
  detachViewportSyncListeners = null
  resizeObserver?.disconnect()
  resizeObserver = null
  if (resizeRaf !== 0) {
    window.cancelAnimationFrame(resizeRaf)
    resizeRaf = 0
  }
  engine.value?.deactivateTool('cad')
  dispose()
})
</script>

<style scoped>
.cad-dual-page {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 392px;
  height: 100dvh;
  min-height: 100vh;
  overflow: hidden;
  background:
    radial-gradient(circle at top left, rgba(68, 113, 188, 0.16), transparent 30%),
    radial-gradient(circle at bottom right, rgba(205, 133, 63, 0.12), transparent 26%),
    #08111a;
  color: #edf5fb;
}

.cad-dual-stage-column {
  display: flex;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  gap: 14px;
  padding: 16px 0 16px 16px;
}

.cad-dual-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
   padding: 0 6px 0 2px;
}

.cad-dual-copy {
  min-width: 0;
}

.cad-dual-kicker {
  margin: 0;
  color: #8fd9ff;
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.cad-dual-title {
  margin: 10px 0 0;
  font-size: clamp(26px, 3vw, 38px);
  line-height: 1.05;
}

.cad-dual-description {
  max-width: 780px;
  margin: 10px 0 0;
  color: rgba(228, 238, 247, 0.72);
  font-size: 13px;
  line-height: 1.7;
}

.cad-dual-pill-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.cad-dual-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 30px;
  padding: 5px 10px;
  border: 1px solid rgba(123, 217, 255, 0.22);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  color: #dff4ff;
  font-size: 11px;
  white-space: nowrap;
}

.cad-dual-pill-accent {
  border-color: rgba(255, 191, 124, 0.26);
  color: #ffd7af;
}

.cad-dual-stage-shell {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 28px;
  background:
    radial-gradient(circle at top, rgba(78, 123, 209, 0.12), transparent 40%),
    linear-gradient(180deg, rgba(7, 12, 20, 0.96), rgba(3, 6, 11, 0.98));
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.03);
}

.cad-dual-canvas-host {
  position: absolute;
  inset: 0;

  :deep(canvas) {
    outline: none;
  }

  &:focus,
  &:focus-visible {
    outline: none;
  }
}

.cad-dual-viewport-layout {
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  padding: 14px;
  pointer-events: none;
}

.cad-dual-viewport-frame {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0)),
    rgba(255, 255, 255, 0.01);
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.03),
    inset 0 0 46px rgba(255, 255, 255, 0.03);
}

.cad-dual-viewport-frame::after {
  position: absolute;
  inset: 0;
  content: '';
  border-radius: inherit;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.02);
}

.cad-dual-viewport-frame.active {
  border-color: color-mix(in srgb, var(--view-accent) 72%, white 10%);
  box-shadow:
    inset 0 0 0 1px color-mix(in srgb, var(--view-accent) 24%, transparent),
    0 0 0 1px color-mix(in srgb, var(--view-accent) 18%, transparent);
}

.cad-dual-viewport-tag,
.cad-dual-viewport-hint {
  position: absolute;
  z-index: 2;
}

.cad-dual-viewport-tag {
  top: 12px;
  right: 12px;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  max-width: calc(100% - 24px);
  padding: 10px 12px;
  border: 1px solid color-mix(in srgb, var(--view-accent) 28%, white 8%);
  border-radius: 14px;
  background: rgba(6, 11, 18, 0.72);
  color: #eff7fd;
  text-align: right;
  cursor: pointer;
  pointer-events: auto;
  backdrop-filter: blur(12px);
}

.cad-dual-viewport-tag-title {
  font-size: 13px;
  font-weight: 700;
}

.cad-dual-viewport-tag-meta {
  color: rgba(231, 239, 247, 0.72);
  font-size: 11px;
  line-height: 1.5;
}

.cad-dual-viewport-hint {
  right: 12px;
  bottom: 12px;
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(5, 10, 17, 0.68);
  color: rgba(231, 239, 247, 0.76);
  font-size: 11px;
  backdrop-filter: blur(12px);
}

.cad-dual-panel-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

@media (max-width: 1280px) {
  .cad-dual-page {
    grid-template-columns: minmax(0, 1fr) 360px;
  }
}

@media (max-width: 1080px) {
  .cad-dual-page {
    grid-template-columns: minmax(0, 1fr);
  }

  .cad-dual-stage-column {
    padding-right: 16px;
  }
}

@media (max-width: 880px) {
  .cad-dual-header {
    flex-direction: column;
  }

  .cad-dual-pill-list {
    justify-content: flex-start;
  }

  .cad-dual-viewport-layout {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: repeat(2, minmax(0, 1fr));
  }

  .cad-dual-panel-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
